/**
 * זמינות להזמנה — מלאי מחסן (Comax), כמו ProductCardHorizontal.
 */
export function isProductAvailableForOrder(product) {
  if (!product) return false;
  return getMaxCartQuantity(product) >= 1;
}

export function isDept33Product(product) {
  const dept = String(product?.comax_department_code ?? product?.comaxDepartmentCode ?? "").trim();
  return dept === "33";
}

export function isArchivedCatalogProduct(product) {
  return !!(product?.is_archived || product?.isArchived);
}

export function isProductReturnedToStock(product) {
  const until = product?.returned_to_stock_until ?? product?.returnedToStockUntil;
  return !!(until && new Date(until) > new Date());
}

export function isProductReturnedToAssortment(product, centralAccountId) {
  const central = String(centralAccountId ?? "").trim();
  if (!central) return false;
  const raw = product?.returned_to_assortment_until ?? product?.returnedToAssortmentUntil;
  if (!raw || typeof raw !== "object") return false;
  const until = raw[central];
  return !!(until && new Date(until) > new Date());
}

/** האם ניתן להזמין — מלאי מחסן שמספיק למארז, או חריג «חזר למלאי». «חזר למגוון» הוא תווית בלבד. */
export function isProductOrderable(product, { centralAccountId } = {}) {
  if (!product) return false;
  if (product?.to_be_deleted || product?.toBeDeleted) return false;
  if (isArchivedCatalogProduct(product)) return false;
  if (isDept33Product(product)) return false;
  if (isProductAvailableForOrder(product)) return true;
  if (isProductReturnedToStock(product)) return true;
  return false;
}

export function getUnitsInCarton(product) {
  const n = Number(product?.unitsInCarton ?? product?.units_in_carton ?? 1);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
}

export function getWarehouseStockUnits(product) {
  return Math.max(0, Number(product?.stock_quantity ?? product?.stockQuantity ?? 0));
}

/** מקסימום מארזים להזמנה לפי מלאי מחסן (יחידות ÷ יח׳ במארז) */
export function getMaxCartQuantity(product) {
  const units = getWarehouseStockUnits(product);
  if (units <= 0) return 0;
  return Math.floor(units / getUnitsInCarton(product));
}

/**
 * מגביל כמות במארזים. כשאין מלאי — רק הפחתה מהכמות שכבר בסל.
 * @returns {{ quantity: number, capped: boolean }}
 */
export function clampCartQuantity(product, requestedQty, previousQty = 0) {
  const requested = Math.max(0, Math.floor(Number(requestedQty)) || 0);
  const prev = Math.max(0, Math.floor(Number(previousQty)) || 0);
  if (requested <= 0) return { quantity: 0, capped: false };

  if (isProductReturnedToStock(product)) {
    return { quantity: requested, capped: false };
  }

  const max = getMaxCartQuantity(product);
  if (max <= 0) {
    const quantity = requested <= prev ? requested : prev;
    return { quantity, capped: requested > quantity };
  }
  if (requested > max) {
    return { quantity: max, capped: true };
  }
  return { quantity: requested, capped: false };
}

export function getProductCompanyId(product) {
  return String(product?.company_id?._id ?? product?.company_id ?? "").trim();
}

/** ממיין מערך: ניתנים להזמנה קודם, לא ניתנים בסוף — שומר סדר יחסי בתוך כל קבוצה */
export function sortProductsAvailableFirst(products, opts = {}) {
  if (!products?.length) return products ?? [];
  const available = [];
  const unavailable = [];
  for (const p of products) {
    if (isProductOrderable(p, opts)) available.push(p);
    else unavailable.push(p);
  }
  return [...available, ...unavailable];
}

/**
 * בתוך זמינים: מוצרי חברות מקודמות קודם; לא-זמינים בסוף.
 * promotedCompanyIds — מזהי Company (strings) מ-SystemSetting promoted_company_ids
 */
export function sortProductsPromotedAvailableFirst(products, promotedCompanyIds = [], opts = {}) {
  if (!products?.length) return products ?? [];
  const promotedSet = new Set((promotedCompanyIds || []).map(String));
  if (promotedSet.size === 0) return sortProductsAvailableFirst(products, opts);

  const availablePromoted = [];
  const availableOther = [];
  const unavailable = [];

  for (const p of products) {
    if (!isProductOrderable(p, opts)) {
      unavailable.push(p);
      continue;
    }
    const cid = getProductCompanyId(p);
    if (cid && promotedSet.has(cid)) availablePromoted.push(p);
    else availableOther.push(p);
  }

  return [...availablePromoted, ...availableOther, ...unavailable];
}

/** מיון תצוגה סטנדרטי — עם קידום חברות אם הוגדר */
export function applyProductDisplaySort(products, promotedCompanyIds = [], opts = {}) {
  return sortProductsPromotedAvailableFirst(products, promotedCompanyIds, opts);
}
